import React, { useState } from 'react';
import bugsnagClient from '../../../config/bugsnag';
import type { Event as BugsnagEvent } from '@bugsnag/js';
import BugsnagPerformance from '@bugsnag/browser-performance';

// PILOT: this page is the reference Bootstrap -> Tailwind conversion. It uses only
// Tailwind (tw- prefixed) utilities + design tokens from tailwind.config.js. See
// docs/tailwind-migration.md for the react-bootstrap -> Tailwind recipe used here.

// Small local primitives keep the JSX readable and model the shared components a full
// migration would extract into client/src/components.
const btnBase =
  'tw-w-full tw-rounded tw-px-4 tw-py-3 tw-text-white tw-font-medium tw-transition ' +
  'hover:tw-brightness-95 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed';

const Alert: React.FC<{ variant: 'info' | 'warning' | 'success'; className?: string; children: React.ReactNode }> = ({
  variant,
  className = '',
  children,
}) => {
  const styles: Record<string, string> = {
    info: 'tw-bg-[#cff4fc] tw-text-[#055160] tw-border-[#b6effb]',
    warning: 'tw-bg-warning tw-text-warning-text tw-border-[#ffecb5]',
    success: 'tw-bg-[#d1e7dd] tw-text-[#0f5132] tw-border-[#badbcc]',
  };
  return (
    <div className={`tw-rounded tw-border tw-p-3 ${styles[variant]} ${className}`} role="alert">
      {children}
    </div>
  );
};

const BugsnagTestPage: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  // Helper function to build API URL
  const buildApiUrl = (path: string) => {
    const apiBase = process.env.REACT_APP_API_URL || '';

    // If no API base, use relative URL (proxy will handle it)
    if (!apiBase) {
      return `/api${path}`;
    }

    // If API base already ends with /api, don't add it again
    if (apiBase.endsWith('/api') || apiBase.endsWith('/api/')) {
      return `${apiBase.replace(/\/$/, '')}${path}`;
    }

    // Otherwise, add /api
    return `${apiBase.replace(/\/$/, '')}/api${path}`;
  };

  // Test 1: Frontend Error
  const handleFrontendError = () => {
    addResult('Triggering frontend error...');
    bugsnagClient.notify(new Error('[BugsnagTest] Frontend error test'), (event: BugsnagEvent) => {
      event.severity = 'error';
      event.unhandled = false;
      event.addMetadata('test', {
        type: 'frontend-error',
        location: 'browser',
      });
      return true;
    });
    addResult('✅ Frontend error sent to Bugsnag');
  };

  // Test 2: Backend Error
  const handleBackendError = async () => {
    setIsLoading(true);
    addResult('Triggering backend error...');
    try {
      const url = buildApiUrl('/test-bugsnag?type=crash');

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        addResult(`✅ Backend error sent: ${data.status}`);
      } else {
        addResult(`✅ Backend error triggered (expected): ${response.status}`);
      }
    } catch (error) {
      addResult(`✅ Backend error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Test 3: Distributed Trace Error - Connects frontend error + backend error in same trace
  const handleDistributedError = async () => {
    setIsLoading(true);
    addResult('Triggering distributed trace error (frontend error + backend error in same trace)...');

    try {
      const url = buildApiUrl('/test-bugsnag?type=distributed-error');
      addResult(`Making request to: ${url}`);
      console.log('[BugsnagTest] Starting distributed trace test - fetch will be automatically instrumented');

      // The fetch request will be automatically instrumented by Bugsnag Performance
      // This will create a network span and send traceparent headers to the backend
      // We'll trigger a frontend error during/after this request so it's in the same trace context
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      // Trigger frontend error AFTER the network request but in the same execution context
      // This ensures the error is linked to the network span (same trace)
      addResult('Triggering frontend error in same trace context...');
      bugsnagClient.notify(
        new Error('[BugsnagTest] Frontend error in distributed trace - This should link to the network span'),
        (event: BugsnagEvent) => {
          event.severity = 'error';
          event.unhandled = false;
          event.addMetadata('testHarness', {
            location: 'frontend',
            type: 'distributed-error-frontend',
            note: 'This error should be in the same distributed trace as the backend error',
          });
          return true;
        }
      );
      addResult('✅ Frontend error sent to Bugsnag');

      if (response.ok) {
        const data = await response.json();
        addResult(`Unexpected success: ${data.status}`);
      } else {
        addResult(`✅ Backend error triggered (expected): ${response.status}`);
        addResult('✅ Both frontend and backend errors should be in the same distributed trace');
      }
    } catch (error) {
      // Also trigger error in catch block - this ensures error is in trace context even if network fails
      addResult(`Network error occurred: ${error instanceof Error ? error.message : 'Unknown error'}`);
      addResult('Triggering frontend error in catch block (same trace context)...');

      bugsnagClient.notify(
        new Error('[BugsnagTest] Frontend network error in distributed trace - This should link to the failed network span'),
        (event: BugsnagEvent) => {
          event.severity = 'error';
          event.unhandled = false;
          event.addMetadata('testHarness', {
            location: 'frontend',
            type: 'distributed-error-network',
            originalError: error instanceof Error ? error.message : 'Unknown',
            note: 'This error should be in the same distributed trace as the backend error',
          });
          return true;
        }
      );
      addResult('✅ Frontend error sent to Bugsnag');
      addResult('✅ Both frontend and backend errors should be in the same distributed trace');
    } finally {
      setIsLoading(false);
    }
  };

  // Test 4: Active Span Error - Report error while span is active (with distributed tracing)
  const handleActiveSpanError = async () => {
    setIsLoading(true);
    addResult('Creating active span for distributed tracing...');

    // Create a span that will stay active during the entire operation
    // This span will be visible in the distributed trace
    const testSpan = BugsnagPerformance.startSpan('rum-test');
    addResult('✅ Span created: rum-test (active)');

    try {
      // Make a backend API call - this will propagate trace context and create backend spans
      // The rum-test span will be the parent span in the distributed trace
      const url = buildApiUrl('/test-bugsnag?type=distributed-error');
      addResult(`Making request to backend: ${url}`);
      addResult('⚠️ rum-test span is ACTIVE - you should see it in the distributed trace');

      // Notify Bugsnag of a handled error while the span is active
      // This error will be linked to the active rum-test span
      addResult('Reporting error to Bugsnag (span is active)...');
      bugsnagClient.notify(
        new Error('[BugsnagTest] Handled error for testing - reported while rum-test span is active'),
        (event: BugsnagEvent) => {
          event.severity = 'error';
          event.unhandled = false;
          event.addMetadata('test', {
            type: 'active-span-error',
            location: 'browser',
            spanName: 'rum-test',
            note: 'This error was reported while the rum-test span was still active - should appear in distributed trace',
          });
          return true;
        }
      );
      addResult('✅ Error sent to Bugsnag (while span is active)');

      // Make the backend call - trace context will be automatically propagated
      // The backend will create spans that are children of the rum-test span
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });

      addResult('✅ Backend request completed');

      if (response.ok) {
        const data = await response.json();
        addResult(`Backend response: ${data.status}`);
      } else {
        addResult(`✅ Backend error triggered (expected): ${response.status}`);
      }

      // Keep the span active for a bit longer so it's visible in the distributed trace
      // This ensures the span appears in Bugsnag's trace view
      setTimeout(() => {
        testSpan.end();
        addResult('✅ Span ended after 300ms');
        addResult('✅ Test complete - check Bugsnag for distributed trace with active rum-test span');
        setIsLoading(false);
      }, 300);
    } catch (error) {
      addResult(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Still report error while span is active
      bugsnagClient.notify(
        new Error('[BugsnagTest] Network error while rum-test span is active'),
        (event: BugsnagEvent) => {
          event.severity = 'error';
          event.unhandled = false;
          event.addMetadata('test', {
            type: 'active-span-error-network',
            location: 'browser',
            spanName: 'rum-test',
            originalError: error instanceof Error ? error.message : 'Unknown',
          });
          return true;
        }
      );

      setTimeout(() => {
        testSpan.end();
        setIsLoading(false);
      }, 300);
    }
  };

  // Check if we're in production (should disable tests)
  const isProduction = process.env.NODE_ENV === 'production' ||
                      process.env.REACT_APP_ENVIRONMENT === 'production';

  if (isProduction) {
    return (
      <div className="tw-mx-auto tw-mt-6 tw-max-w-5xl tw-px-4">
        <Alert variant="warning">
          <h4 className="tw-mb-2 tw-text-lg tw-font-semibold">Bugsnag Test Page Disabled</h4>
          <p className="tw-mb-0">This test page is disabled in production for security reasons.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="tw-mx-auto tw-mt-6 tw-max-w-5xl tw-px-4">
      <div className="tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-line tw-bg-surface-alt tw-px-4 tw-py-3">
          <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content">🐛 Bugsnag Error Tests</h2>
          <span className="tw-inline-block tw-rounded tw-bg-[#0dcaf0] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-black">
            Testing Environment Only
          </span>
        </div>
        <div className="tw-p-4">
          <Alert variant="info">
            <strong>Instructions:</strong> Click any button below to trigger a test error. Check your Bugsnag
            dashboard to verify the error was received.
          </Alert>

          <div className="tw-mt-4 tw-grid tw-gap-3">
            <button className={`${btnBase} tw-bg-danger`} onClick={handleFrontendError} disabled={isLoading}>
              Test 1: Frontend Error
            </button>

            <button className={`${btnBase} tw-bg-overtime`} onClick={handleBackendError} disabled={isLoading}>
              Test 2: Backend Error
            </button>

            <button className={`${btnBase} tw-bg-primary`} onClick={handleDistributedError} disabled={isLoading}>
              Test 3: Distributed Trace Error (Frontend + Backend)
            </button>

            <button className={`${btnBase} tw-bg-[#0dcaf0] !tw-text-black`} onClick={handleActiveSpanError} disabled={isLoading}>
              Test 4: Active Span Error (Error reported while span is active)
            </button>
          </div>

          <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-line tw-bg-surface-alt tw-px-4 tw-py-3">
              <h5 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Test Results</h5>
              <button
                className="tw-rounded tw-border tw-border-content-muted tw-px-3 tw-py-1 tw-text-sm tw-text-content-secondary hover:tw-bg-surface-hover"
                onClick={clearResults}
              >
                Clear
              </button>
            </div>
            <div className="tw-p-4">
              <div className="tw-max-h-[300px] tw-overflow-y-auto tw-rounded tw-bg-surface-alt tw-p-3 tw-font-mono tw-text-sm">
                {testResults.length === 0 ? (
                  <div className="tw-text-content-muted">No test results yet. Click a button above to start testing.</div>
                ) : (
                  testResults.map((result, index) => <div key={index}>{result}</div>)
                )}
              </div>
            </div>
          </div>

          <Alert variant="success" className="tw-mt-4">
            <strong>Next Steps:</strong>
            <ol className="tw-mb-0 tw-list-decimal tw-pl-5">
              <li>Click any test button above</li>
              <li>Open your Bugsnag dashboard</li>
              <li>Check the "Recent Events" section</li>
              <li>Verify the error includes proper metadata (feature, testHarness, user context, etc.)</li>
              <li>Confirm the error severity and classification</li>
              <li>
                <strong>For Test 3:</strong> Check the Performance tab - both frontend and backend errors should
                appear in the same distributed trace
              </li>
            </ol>
          </Alert>
        </div>
      </div>
    </div>
  );
};

export default BugsnagTestPage;
